import { useEffect, useState } from "react";
import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { useServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { verifyCheckoutSession } from "@/lib/stripe-checkout.functions";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle2, Loader2, AlertOctagon, ArrowRight, Package, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatGBP } from "@/lib/format";
import { track } from "@/lib/analytics";
import { useTranslation } from "@/i18n";

export const Route = createFileRoute("/_authenticated/orders/success")({
  head: () => ({ meta: [{ title: "Payment received — BidNova Auctions" }] }),
  validateSearch: (s) =>
    z.object({ order_id: z.string().uuid(), session_id: z.string() }).parse(s),
  component: SuccessPage,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

type OrderSummary = {
  id: string;
  amount_pence: number;
  shipping_cost_pence: number | null;
  currency: string | null;
  shipping_method: string | null;
  auctions: { title: string; images: string[] | null } | null;
};

function SuccessPage() {
  const { t } = useTranslation("commerce");
  const { order_id, session_id } = useSearch({ from: "/_authenticated/orders/success" });
  const verify = useServerFn(verifyCheckoutSession);
  const [state, setState] = useState<"loading" | "paid" | "pending" | "error">("loading");
  const [msg, setMsg] = useState<string>("");
  const [order, setOrder] = useState<OrderSummary | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await verify({ data: { orderId: order_id, sessionId: session_id } });
        if (cancelled) return;
        setState(res.paid ? "paid" : "pending");
        if (!res.paid) setMsg(t("success.pendingStatus", "Payment status: {status}", { status: res.status }));
        if (res.paid) {
          track("purchase", { order_id });
          const { data } = await supabase
            .from("orders")
            .select("id,amount_pence,shipping_cost_pence,currency,shipping_method,auctions(title,images)")
            .eq("id", order_id)
            .maybeSingle();
          if (!cancelled && data) setOrder(data as unknown as OrderSummary);
        }
      } catch (e) {
        if (cancelled) return;
        setState("error");
        setMsg((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [order_id, session_id, verify]);

  if (state === "loading") {
    return (
      <div className="mx-auto max-w-lg px-4 py-24 text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-gold" />
        <h1 className="mt-4 font-display text-2xl">{t("success.confirming", "Confirming your payment…")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("success.confirmingHint", "Hang tight — this usually takes a second.")}</p>
      </div>
    );
  }

  if (state === "pending") {
    return (
      <div className="mx-auto max-w-lg px-4 py-24 text-center">
        <Loader2 className="h-10 w-10 animate-spin mx-auto text-amber-400" />
        <h1 className="mt-4 font-display text-2xl">{t("success.pendingTitle", "Payment still processing")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{msg || t("success.pendingFallback", "Check back in a moment.")}</p>
        <Button asChild variant="outline" className="mt-6">
          <Link to="/orders">{t("success.goToOrders", "Go to my orders")}</Link>
        </Button>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="mx-auto max-w-lg px-4 py-24 text-center">
        <AlertOctagon className="h-10 w-10 mx-auto text-red-400" />
        <h1 className="mt-4 font-display text-2xl">{t("success.errorTitle", "Something went wrong")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{msg}</p>
        <p className="mt-1 text-xs text-muted-foreground">
          {t("success.orderReference", "Order reference:")} <span className="font-mono">{order_id.slice(0, 8)}</span>
        </p>
        <div className="mt-6 flex gap-2 justify-center">
          <Button asChild variant="outline">
            <Link to="/orders">{t("success.myOrders", "My orders")}</Link>
          </Button>
          <Button asChild variant="ghost">
            <Link to="/legal/buyer-terms">{t("success.contactSupport", "Contact support")}</Link>
          </Button>
        </div>
      </div>
    );
  }

  const image = order?.auctions?.images?.[0];
  const total = (order?.amount_pence ?? 0) + (order?.shipping_cost_pence ?? 0);

  return (
    <div className="mx-auto max-w-2xl px-4 py-16">
      <div className="relative overflow-hidden rounded-2xl border border-emerald-500/30 bg-gradient-to-b from-emerald-500/10 to-transparent p-8 text-center">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full bg-emerald-500/20 blur-3xl"
        />
        <div className="relative">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15 border border-emerald-500/40 animate-scale-in">
            <CheckCircle2 className="h-9 w-9 text-emerald-400" />
          </div>
          <h1 className="mt-5 font-display text-3xl">{t("success.paidTitle", "Payment received")}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("success.paidSubtitle", "Thanks — the seller has been notified and will ship your card shortly.")}
          </p>
          <p className="mt-3 text-xs text-muted-foreground">
            {t("success.orderReferenceInline", "Order reference {ref}", { ref: order_id.slice(0, 8).toUpperCase() })}
          </p>
        </div>
      </div>

      {order && (
        <div className="mt-6 rounded-2xl border border-border/60 bg-card p-6 gold-ring animate-fade-in">
          <div className="flex gap-4">
            {image ? (
              <img
                src={image}
                alt=""
                className="h-20 w-20 rounded-lg object-cover border border-border/60"
              />
            ) : (
              <div className="h-20 w-20 rounded-lg bg-surface-2" />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("success.yourItem", "Your item")}</p>
              <p className="font-medium truncate">{order.auctions?.title ?? t("success.auctionFallback", "Auction")}</p>
              {order.shipping_method && (
                <p className="mt-1 text-xs text-muted-foreground inline-flex items-center gap-1">
                  <Package className="h-3.5 w-3.5" /> {order.shipping_method}
                </p>
              )}
            </div>
          </div>

          <div className="mt-5 space-y-1.5 text-sm border-t border-border/60 pt-4">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("success.itemLabel", "Item")}</span>
              <span>{formatGBP(order.amount_pence)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("success.shippingLabel", "Shipping")}</span>
              <span>{formatGBP(order.shipping_cost_pence ?? 0)}</span>
            </div>
            <div className="flex justify-between pt-2 mt-2 border-t border-border/60">
              <span className="font-medium inline-flex items-center gap-1.5">
                <Receipt className="h-4 w-4" /> {t("success.totalPaid", "Total paid")}
              </span>
              <span className="font-display text-lg text-gold-gradient">{formatGBP(total)}</span>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <Button asChild className="bg-gold-gradient hover:opacity-90 press-tap">
          <Link to="/orders">
            {t("success.viewOrder", "View order")} <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </Button>
        <Button asChild variant="outline" className="press-tap">
          <Link to="/auctions">{t("success.continueBrowsing", "Continue browsing")}</Link>
        </Button>
      </div>

      <p className="mt-6 text-center text-xs text-muted-foreground">
        {t("success.receiptEmailed", "A receipt and invoice have been emailed to you.")}
      </p>
    </div>
  );
}
